import { useEffect, useState } from "react";

import { SessionStorage } from "@/helpers/session-storage";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { EmailDraftI } from "@/components/modules/email-templates-sidebar/interfaces";

export const EMAIL_TEMPLATE_LOCAL_DRAFTS_KEY_TYPES = {
  CALL_ID: "call_id",
} as const;

const getSessionStorageLocalDrafts = (
  type: ValueOfObjectFields<typeof EMAIL_TEMPLATE_LOCAL_DRAFTS_KEY_TYPES>
) => {
  if (typeof window !== "undefined") {
    const SS = new SessionStorage();

    switch (type) {
      case EMAIL_TEMPLATE_LOCAL_DRAFTS_KEY_TYPES.CALL_ID:
        return SS.emailLocalDraftsByCallId || {};
      default:
        return {};
    }
  }

  return {};
};

export interface EmailTemplateDraftsI {
  drafts: Record<string, EmailDraftI>;
  setDrafts: React.Dispatch<React.SetStateAction<Record<string, EmailDraftI>>>;
  setDraft: (draftId: string, emailData?: EmailDraftI) => void;
  getDraft: (draftId: string) => EmailDraftI | undefined;
}

/**
 * Hook to manage email drafts
 * @param drafts - initial drafts
 * @returns Interface containing drafts state and methods to manage it
 */
export const useEmailTemplateDrafts = (config?: {
  drafts?: Record<string, EmailDraftI>;
}): EmailTemplateDraftsI => {
  const [drafts, setDrafts] = useState<Record<string, EmailDraftI>>(
    config?.drafts || {}
  );

  const setDraft = (draftId: string, emailData?: EmailDraftI) => {
    setDrafts((prev) => ({
      ...prev,
      [draftId]: { ...(emailData || {}) },
    }));
  };

  const getDraft = (draftId: string) => drafts[draftId];

  return {
    drafts,
    setDrafts,
    setDraft,
    getDraft,
  };
};

/**
 * Session storage drafts for email templates
 * This is used to save email data locally when user is composing email
 * so that it can be used later when user comes back to the email template
 */
export const useEmailTemplateSSDrafts = (
  type: ValueOfObjectFields<typeof EMAIL_TEMPLATE_LOCAL_DRAFTS_KEY_TYPES>
) => {
  const {
    drafts: localDrafts,
    setDraft: setLocalDraft,
    setDrafts: setLocalDrafts,
  } = useEmailTemplateDrafts({
    drafts: getSessionStorageLocalDrafts(type),
  });

  const getLocalDraft = (draftId: string) => {
    const drafts = getSessionStorageLocalDrafts(type);

    return drafts?.[draftId];
  };

  useEffect(() => {
    const SS = new SessionStorage();

    switch (type) {
      case EMAIL_TEMPLATE_LOCAL_DRAFTS_KEY_TYPES.CALL_ID:
        SS.emailLocalDraftsByCallId = localDrafts;
        break;
      default:
        break;
    }
  }, [localDrafts]);

  return {
    localDrafts,
    setLocalDrafts,
    /**
     * Set local draft for a given draft id
     * @param draftId - id of the draft
     * @param emailData - email data to be saved
     */
    setLocalDraft,
    /**
     * Get local draft for a given draft id
     * @param draftId - id of the draft
     * @returns email data
     */
    getLocalDraft,
  };
};
